import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Download, AlertCircle, Inbox, ArrowLeft } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';
import { useConfirm } from './ui';

type LmsType = 'moodle' | 'tutorlms' | 'learndash' | 'xtramile';

interface RequestRow {
  lms_type: LmsType;
  integration_id: string;
  member_name: string;
  member_email: string;
  forbund_name: string;
  requester_name: string;
  requester_email: string;
  approval_requested_at: string | null;
  approved_by_forbund: boolean;
  approved_by_forbund_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  connection_tested: boolean;
  agreement_confirmed: boolean;
  agreement_pdf_path: string | null;
}

interface Signature {
  party: 'member' | 'forbund';
  signed_by_name: string;
  signed_by_email: string;
  signed_by_role: string;
  organization_name: string;
  ip_address: string;
  signed_at: string;
  rejection_reason: string | null;
}

interface Agreement {
  template_type: string;
  template_name: string;
  content: string;
  version: string;
}

interface RequestDetail {
  integration: any;
  signatures: Signature[];
  agreements: Agreement[];
}

const LMS_DISPLAY: Record<LmsType, string> = {
  moodle: 'Moodle',
  tutorlms: 'TutorLMS',
  learndash: 'LearnDash',
  xtramile: 'Xtramile'
};

const LMS_COLOR: Record<LmsType, string> = {
  moodle: 'bg-kf-orange-100 text-kf-orange-800 border-kf-orange-300',
  tutorlms: 'bg-purple-100 text-purple-800 border-purple-300',
  learndash: 'bg-green-100 text-green-800 border-green-300',
  xtramile: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-300'
};

export default function IntegrationApprovalReview() {
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [selected, setSelected] = useState<RequestRow | null>(null);
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const { confirm } = useConfirm();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { loadRequests(); }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const url = filter === 'pending' ? '/api/lms-approval/pending' : '/api/lms-approval/all';
      const r = await authenticatedFetch(url);
      const data = await r.json();
      setRequests(data.requests || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (row: RequestRow) => {
    setSelected(row);
    setDetail(null);
    setShowRejectForm(false);
    setRejectionReason('');
    setError(null);
    setLoadingDetail(true);
    try {
      const r = await authenticatedFetch(`/api/lms-approval/request/${row.lms_type}/${row.integration_id}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Kunne ikke hente detaljer');
      setDetail(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: `Godkjenne ${LMS_DISPLAY[selected.lms_type]}-integrasjon?`,
      message: `Integrasjon for ${selected.member_name} vil bli aktivert. Signert PDF-avtale genereres og sendes til begge parter.`,
      variant: 'info',
      confirmLabel: 'Godkjenn integrasjon',
    });
    if (!ok) return;
    setActionInProgress(true);
    setError(null);
    try {
      const r = await authenticatedFetch(
        `/api/${selected.lms_type}/integration-requests/${selected.integration_id}/approve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Godkjenning feilet');
      setSelected(null);
      await loadRequests();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (rejectionReason.trim().length < 10) {
      setError('Begrunnelse må være minst 10 tegn');
      return;
    }
    setActionInProgress(true);
    setError(null);
    try {
      const r = await authenticatedFetch(
        `/api/${selected.lms_type}/integration-requests/${selected.integration_id}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rejectionReason })
        }
      );
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.error || 'Avslag feilet');
      setSelected(null);
      await loadRequests();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const getStatus = (r: RequestRow) => {
    if (r.approved_by_forbund) return { label: 'Godkjent', icon: CheckCircle, color: 'text-green-600' };
    if (r.rejected_at) return { label: 'Avslått', icon: XCircle, color: 'text-red-600' };
    if (r.approval_requested_at) return { label: 'Venter', icon: Clock, color: 'text-yellow-600' };
    return { label: 'Utkast', icon: AlertCircle, color: 'text-gray-500' };
  };

  // Detail view
  if (selected) {
    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">Forespørsel fra {selected.member_name}</h2>
          <span className={`px-2 py-1 text-xs font-medium rounded border ${LMS_COLOR[selected.lms_type]}`}>
            {LMS_DISPLAY[selected.lms_type]}
          </span>
        </div>

        <div className="p-6 space-y-6">
          {loadingDetail && <p className="text-gray-500">Laster detaljer...</p>}
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

          {detail && (
            <>
              {/* Context summary */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Medlemsorganisasjon</p>
                  <p className="font-medium">{selected.member_name}</p>
                  <p className="text-sm text-gray-600">{selected.member_email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Forespurt av</p>
                  <p className="font-medium">{selected.requester_name || '—'}</p>
                  <p className="text-sm text-gray-600">{selected.requester_email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Sendt inn</p>
                  <p>{selected.approval_requested_at ? new Date(selected.approval_requested_at).toLocaleString('nb-NO') : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Tilkoblingstest</p>
                  <p className={selected.connection_tested ? 'text-green-700' : 'text-red-700'}>
                    {selected.connection_tested ? '✓ Bestått' : '✗ Ikke bestått'}
                  </p>
                </div>
              </div>

              {/* Agreement texts */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Avtaletekster (bekreftet av medlem)</h3>
                <div className="space-y-3">
                  {detail.agreements.map(a => (
                    <details key={a.template_type} className="border rounded bg-white">
                      <summary className="px-4 py-2 cursor-pointer font-medium text-gray-800 hover:bg-gray-50">
                        {a.template_name}
                        <span className="ml-2 text-xs text-gray-500">v{a.version}</span>
                      </summary>
                      <div className="px-4 py-3 border-t prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: a.content }} />
                    </details>
                  ))}
                </div>
              </div>

              {/* Signatures so far */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Signaturer</h3>
                {detail.signatures.length === 0 ? (
                  <p className="text-sm text-gray-500">Ingen signaturer ennå.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.signatures.map((s, i) => (
                      <div key={i} className={`p-3 rounded border text-sm ${s.party === 'member' ? 'bg-kf-blue-50 border-kf-blue-200' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <strong>{s.party === 'member' ? 'Medlem' : 'Studieforbund'}:</strong> {s.signed_by_name} ({s.signed_by_email})
                            <br />
                            <span className="text-xs text-gray-600">{s.organization_name} · IP: {s.ip_address} · {new Date(s.signed_at).toLocaleString('nb-NO')}</span>
                          </div>
                          {s.rejection_reason && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Avslag</span>}
                        </div>
                        {s.rejection_reason && (
                          <p className="mt-2 pl-3 border-l-2 border-red-300 text-gray-700">{s.rejection_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              {selected.approval_requested_at && !selected.approved_by_forbund && !selected.rejected_at && (
                <div className="border-t pt-6">
                  {!showRejectForm ? (
                    <div className="flex gap-3">
                      <button
                        onClick={handleApprove}
                        disabled={actionInProgress}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        {actionInProgress ? 'Behandler...' : 'Godkjenn og signer avtale'}
                      </button>
                      <button
                        onClick={() => setShowRejectForm(true)}
                        disabled={actionInProgress}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 font-medium"
                      >
                        Avslå
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Begrunnelse for avslag (min. 10 tegn)
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Beskriv hvorfor forespørselen avslås..."
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={handleReject}
                          disabled={actionInProgress || rejectionReason.trim().length < 10}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {actionInProgress ? 'Sender...' : 'Bekreft avslag'}
                        </button>
                        <button
                          onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                          className="px-4 py-2 border rounded hover:bg-gray-50"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Already approved - show PDF download */}
              {selected.approved_by_forbund && selected.agreement_pdf_path && (
                <div className="border-t pt-6">
                  <a
                    href={`/api/${selected.lms_type}/integrations/${selected.integration_id}/agreement-pdf`}
                    download
                    className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded hover:bg-kf-blue-700"
                  >
                    <Download className="h-4 w-4" />
                    Last ned signert avtale (PDF)
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h2 className="text-lg font-semibold">LMS-integrasjonsforespørsler</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 text-sm rounded ${filter === 'pending' ? 'bg-kf-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Ventende
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm rounded ${filter === 'all' ? 'bg-kf-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Alle
          </button>
        </div>
      </div>

      <div className="p-6">
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
        {loading ? (
          <p className="text-gray-500">Laster...</p>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">
              {filter === 'pending' ? 'Ingen ventende forespørsler.' : 'Ingen forespørsler.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map(r => {
              const status = getStatus(r);
              const StatusIcon = status.icon;
              return (
                <button
                  key={`${r.lms_type}-${r.integration_id}`}
                  onClick={() => loadDetail(r)}
                  className="w-full text-left p-4 border rounded hover:border-kf-blue-400 hover:bg-kf-blue-50 transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${LMS_COLOR[r.lms_type]}`}>
                          {LMS_DISPLAY[r.lms_type]}
                        </span>
                        <span className="font-medium text-gray-900">{r.member_name}</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Forespurt av {r.requester_name || r.requester_email} ·{' '}
                        {r.approval_requested_at ? new Date(r.approval_requested_at).toLocaleDateString('nb-NO') : '—'}
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 ${status.color} text-sm font-medium`}>
                      <StatusIcon className="h-4 w-4" />
                      {status.label}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
